import { useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import {
  ButtonUI,
  Checkbox,
  CustomThemeProvider,
  Prompt,
  SelectionableSection,
  usePortal
} from 'sonata-react-components';
import { useTheme } from 'styled-components';

import './selectionableSectionWithConfirmation.scss';

type Props = React.ComponentProps<typeof SelectionableSection> & {
  modalConfirm: {
    description?: string;
    storageKey?: string;
    textButton?: string;
    title?: string;
  };
};

type State = {
  doNotShowAgain: boolean;
  isSectionEnabled: boolean;
  showPrompt: boolean;
};

export function SelectionableSectionWithConfirmation({
  children,
  id,
  isSectionEnabled: isSectionEnabledProp,
  modalConfirm,
  onSectionEnabledChange,
  ...selectionalbleSectionRestProps
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { name: themeName }: Record<string, any> = useTheme();
  const theme = themeName.toLowerCase();

  const { description, storageKey, textButton, title } = modalConfirm;

  const doNotDisturb = storageKey && localStorage.getItem(storageKey);

  const [{ isSectionEnabled, showPrompt, doNotShowAgain }, setState] = useState<State>({
    doNotShowAgain: false,
    isSectionEnabled: Boolean(isSectionEnabledProp),
    showPrompt: false
  });

  const handleAcceptClick = () => {
    setState(prev => ({ ...prev, isSectionEnabled: false, showPrompt: false }));
    onSectionEnabledChange?.(false);
  };

  const handleClosePrompt = () => {
    setState(prev => ({
      ...prev,
      doNotShowAgain: false,
      showPrompt: false
    }));
  };

  const handleDoNotShowAgainClick = () => {
    setState(prev => ({ ...prev, isSectionEnabled: false, showPrompt: false }));
    onSectionEnabledChange?.(false);
    storageKey && localStorage.setItem(storageKey, 'true');
  };

  const handleConfirmClick = () => {
    if (doNotShowAgain && storageKey) {
      handleDoNotShowAgainClick();
    } else {
      handleAcceptClick();
    }
  };

  const targetBodyPrompt = usePortal('selectionableSectionWithConfirmation_prompt');

  if (showPrompt) {
    return ReactDOM.createPortal(
      <CustomThemeProvider themeContrast={theme}>
        <Prompt className="selectionableSectionWithConfirmation__prompt" closePrompt={handleClosePrompt} width="770px">
          <Prompt.Icon icon="selectionableSectionWithConfirmation__promptIcon fas fa-exclamation-triangle" />
          <Prompt.Title variant="warning">
            {title ??
              formatMessage({
                defaultMessage: 'Disabling this section will permanently delete your recent edits.',
                id: 'targeting.common.confirmPrompt.title'
              })}
          </Prompt.Title>
          <Prompt.Description variant="warning">
            {description ??
              formatMessage({
                defaultMessage: 'Are you sure you want to proceed?',
                id: 'targeting.common.confirmPrompt.description'
              })}
          </Prompt.Description>
          <div className="selectionableSectionWithConfirmation__promptCheckbox">
            {storageKey && (
              <Checkbox
                active={doNotShowAgain}
                id={'selectionableSectionWithConfirmation_doNotShowAgain'}
                labelText={formatMessage({
                  defaultMessage: "Don't show this warning again",
                  id: 'targeting.common.confirmPrompt.doNotShowAgain'
                })}
                onClickAction={(_, checked) => setState(prev => ({ ...prev, doNotShowAgain: checked }))}
              />
            )}
          </div>
          <Prompt.Buttons position="centered">
            <ButtonUI
              onClick={handleClosePrompt}
              shape="squareRounded"
              text={formatMessage({ defaultMessage: 'Cancel', id: 'common.cancel' })}
              type="secondary"
            />
            <ButtonUI
              onClick={handleConfirmClick}
              shape="squareRounded"
              text={
                textButton ??
                formatMessage({
                  defaultMessage: 'Disable',
                  id: 'common.disable'
                })
              }
              type="primary"
            />
          </Prompt.Buttons>
        </Prompt>
      </CustomThemeProvider>,
      targetBodyPrompt
    );
  }

  const handleSectionEnabledChange = (enabled: boolean) => {
    if (doNotDisturb) {
      onSectionEnabledChange?.(enabled);
      return;
    }
    setState(prev => ({
      ...prev,
      showPrompt: !enabled,
      ...(enabled && { isSectionEnabled: enabled })
    }));
    enabled && onSectionEnabledChange?.(true);
  };

  return (
    <SelectionableSection
      id={id}
      isSectionEnabled={isSectionEnabled}
      onSectionEnabledChange={handleSectionEnabledChange}
      {...selectionalbleSectionRestProps}
    >
      {children}
    </SelectionableSection>
  );
}
